import { createApi } from "@reduxjs/toolkit/query/react";
import { APP_CONFIG } from "../../constants/constants";
import { ListingResponse } from "../types/Listing";
import {
  AddMaintenanceDetailsPayload,
  EditMaintenanceDetailsPayload,
  LegacyMaintenanceEntityListingPayload,
  MaintenanceFaultCodePayload,
  MaintenanceFaultCodeResponse,
  SingleOrganizationMaintenancePayload,
  TLegacyMaintenance,
  TSingleLegacyMaintenance,
} from "../types/Maintenance";
import { appBaseQuery, baseQueryWithAuthHandling } from "../utils/utils";
import {
  getAccountConfiguredAssetSearchFields,
  getSearchFieldsParams,
} from "./adminApiServices";
import { API_METHODS } from "./constants";
import { API_ENDPOINTS } from "./endpoints";
import { handleAuthErrorCode } from "./errorCodes";

export enum MaintenanceApiTags {
  MAINTENANCE_CREATED = "MAINTENANCE_CREATED",
  MAINTENANCE_MODIFIED = "MAINTENANCE_MODIFIED",
  MAINTENANCE_DELETED = "MAINTENANCE_DELETED",
  MAINTENANCE_SINGLE = "MAINTENANCE_SINGLE",
}

export const MaintenanceAPIs = createApi({
  reducerPath: "MaintenanceAPI",
  tagTypes: [
    MaintenanceApiTags.MAINTENANCE_CREATED,
    MaintenanceApiTags.MAINTENANCE_DELETED,
    MaintenanceApiTags.MAINTENANCE_MODIFIED,
    MaintenanceApiTags.MAINTENANCE_SINGLE,
  ],
  baseQuery: (args, api, extraOptions) =>
    baseQueryWithAuthHandling(
      appBaseQuery.withAuthJson,
      args,
      api,
      extraOptions
    ),
  endpoints: (builder) => ({
    organizationMaintenance: builder.query<
      ListingResponse<TLegacyMaintenance[]>,
      LegacyMaintenanceEntityListingPayload
    >({
      query: ({
        search_fields,
        ...params
      }: LegacyMaintenanceEntityListingPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ORGANIZATION_MAINTENANCE,
          params: {
            ...params,
            ...getSearchFieldsParams(search_fields, [
              ...getAccountConfiguredAssetSearchFields(),
              ...APP_CONFIG.ENTITY_SEARCH_FIELDS.MAINTENANCE_LISTING,
            ]),
          },
          method: API_METHODS.GET,
        };
      },
      providesTags: [
        MaintenanceApiTags.MAINTENANCE_CREATED,
        MaintenanceApiTags.MAINTENANCE_MODIFIED,
        MaintenanceApiTags.MAINTENANCE_DELETED,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<TLegacyMaintenance[]>) => {
        return response;
      },
    }),

    // single organization maintenance
    singleOrganizationMaintenance: builder.query<
      TSingleLegacyMaintenance,
      SingleOrganizationMaintenancePayload
    >({
      query: ({ accountid, deviceid }) => {
        return {
          url: API_ENDPOINTS.ADMINS.SINGLE_ORGANIZATION_MAINTENANCE(
            accountid,
            deviceid
          ),
          method: API_METHODS.GET,
        };
      },
      providesTags: [MaintenanceApiTags.MAINTENANCE_SINGLE],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TSingleLegacyMaintenance) => {
        return response;
      },
    }),

    //Asset fault code info
    assetMaintenanceFaultCode: builder.query<
      MaintenanceFaultCodeResponse,
      MaintenanceFaultCodePayload
    >({
      query: (params: MaintenanceFaultCodePayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ASSET_MAINTENANCE_FAULT_CODES(
            params?.deviceid
          ),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: MaintenanceFaultCodeResponse) => {
        return response;
      },
    }),

    //edit maintenance details
    editMaintenanceDetails: builder.mutation<
      TSingleLegacyMaintenance,
      EditMaintenanceDetailsPayload
    >({
      query: (params: EditMaintenanceDetailsPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.EDIT_ORGANIZATION_MAINTENANCE(
            params?.deviceid
          ),
          method: API_METHODS.PATCH,
          body: params,
        };
      },
      invalidatesTags: [
        MaintenanceApiTags.MAINTENANCE_MODIFIED,
        MaintenanceApiTags.MAINTENANCE_SINGLE,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TSingleLegacyMaintenance) => {
        return response;
      },
    }),

    // create maintenance details
    addMaintenanceDetails: builder.mutation<
      TSingleLegacyMaintenance,
      AddMaintenanceDetailsPayload
    >({
      query: ({ deviceid, ...params }: AddMaintenanceDetailsPayload) => {
        return {
          url: API_ENDPOINTS.ADMINS.ADD_MAINTENANCE_DETAILS(deviceid),
          method: API_METHODS.POST,
          body: params,
        };
      },
      invalidatesTags: [
        MaintenanceApiTags.MAINTENANCE_CREATED,
        MaintenanceApiTags.MAINTENANCE_SINGLE,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: TSingleLegacyMaintenance) => {
        return response;
      },
    }),
  }),
});

export const {
  useOrganizationMaintenanceQuery,
  useSingleOrganizationMaintenanceQuery,
  useEditMaintenanceDetailsMutation,
  useAssetMaintenanceFaultCodeQuery,
  useLazyAssetMaintenanceFaultCodeQuery,
  useAddMaintenanceDetailsMutation,
} = MaintenanceAPIs;
